import { CachedImage, creatorbase, influencer } from '@round/api';
import { MoneyValue } from '@round/creatorbase-ui';
import { GenericDropdownOption } from 'App.types';
import { isNumber } from 'utility/utility';
import * as yup from 'yup';
import { CreatorAccountRef, ExploreAccount, ExploreAccountDetails } from './types';

export const parseUrlsFromText = (text: string): string[] => {
    const seen = new Set<string>();

    for (const line of text.split(/\r\n|\r|\n/)) {
        const trimmed = line.trim();
        if (trimmed) {
            seen.add(trimmed);
        }
    }

    return Array.from(seen);
};

export type ClassifiedLineGroups = Record<creatorbase.ClassifiedLineStatus, creatorbase.ClassifiedLine[]>;

export const groupClassifiedLines = (lines: creatorbase.ClassifiedLine[]): ClassifiedLineGroups => {
    const groups: ClassifiedLineGroups = { UNPARSEABLE: [], EXISTS: [], NEW: [] };

    for (const line of lines) {
        groups[line.status].push(line);
    }

    return groups;
};

// A line can be pasted without a scheme, which a bare href would resolve against our own origin.
export const toProfileHref = (line: string): string => (/^https?:\/\//i.test(line) ? line : `https://${line}`);

// Each row becomes its own new creator with a single account, so rows are never merged by handle.
export type NewCreatorRow = {
    line: string;
    platform: creatorbase.Platform;
    handle: string;
    cost: MoneyValue | null;
    location: GenericDropdownOption<number> | null;
    content_tags: GenericDropdownOption<number>[];
    contact_details: string;
    paypal_email: string;
    notes: string;
};

export type NewCreatorsFormState = {
    rows: NewCreatorRow[];
};

// Classify always resolves a platform and handle for a new line; a line missing either could not
// be created, so it is left out rather than given a made-up value.
export const toNewCreatorRows = (lines: creatorbase.ClassifiedLine[]): NewCreatorRow[] =>
    lines.flatMap(({ line, platform, handle }) =>
        platform && handle
            ? [
                  {
                      line,
                      platform,
                      handle,
                      cost: null,
                      location: null,
                      content_tags: [],
                      contact_details: '',
                      paypal_email: '',
                      notes: '',
                  },
              ]
            : []
    );

export const toCreatorBulkUploadRow = (row: NewCreatorRow): creatorbase.PostCreatorBulkUploadRow => ({
    // Classify only parses TikTok and Instagram urls.
    platform: row.platform as creatorbase.PostCreatorBulkUploadRow['platform'],
    identifier: row.handle,
    cost: row.cost?.value ? Number(row.cost.value) : null,
    currency_id: row.cost?.currency?.value ?? null,
    location: row.location?.value ?? null,
    content_tags: row.content_tags.map((tag) => tag.value),
    contact_details: row.contact_details.trim(),
    paypal_email: row.paypal_email.trim() || null,
    notes: row.notes.trim(),
});

// The backend stores cost as a decimal with 15 digits, 2 of them after the point.
const MAX_COST = 1e13;

export const getCostError = (cost: MoneyValue | null): string | undefined => {
    const hasAmount = !!cost?.value;
    const hasCurrency = !!cost?.currency;

    if (!hasAmount && !hasCurrency) {
        return 'Enter a cost';
    }

    if (!hasCurrency) {
        return 'Select a currency for this cost';
    }

    if (!hasAmount) {
        return 'Enter an amount for this currency';
    }

    const amount = Number(cost?.value);
    if (Number.isNaN(amount)) {
        return 'Enter a valid cost';
    }

    if (amount < 0) {
        return 'Cost cannot be negative';
    }

    if (amount >= MAX_COST) {
        return 'Cost is too large';
    }

    return undefined;
};

const newCreatorRowSchema = yup.object({
    cost: yup
        .mixed<MoneyValue>()
        .nullable()
        .test('cost', '', function (cost) {
            const message = getCostError(cost ?? null);
            return message ? this.createError({ message }) : true;
        }),
    contact_details: yup.string().trim().required('Enter contact details'),
    content_tags: yup.array().min(1, 'Select at least one tag'),
    paypal_email: yup.string().email().label('PayPal email'),
});

export const newCreatorsValidationSchema = yup.object({
    rows: yup.array().of(newCreatorRowSchema),
});

/** Each platform's account images, keyed by the platform user they belong to. */
export type ImageUrlsByUserId = Record<creatorbase.CreatorBulkUploadItemPlatform, Map<number, string>>;

/**
 * The creators behind the accounts to look up in Creator Explore, once each. An account not saved yet
 * has none, and one creator can come up under several accounts.
 */
export const getExploreClientInfluencerIds = (accounts: CreatorAccountRef[]): string[] => {
    const ids = accounts.map((account) => account.client_influencer_id).filter((id): id is string => !!id);

    return [...new Set(ids)];
};

/**
 * Finds an account among the Creator Explore results, matched on the client influencer first and then
 * on the account within its platform, since one creator can hold several.
 */
const getExploreAccountFinder = (exploreResults: influencer.CreatorSearchResult[]) => {
    const resultsByClientInfluencerId = new Map(
        exploreResults.map((result) => [result.creatorbase_client_influencer_id, result])
    );

    return (account: CreatorAccountRef): ExploreAccount | null => {
        const result = account.client_influencer_id
            ? resultsByClientInfluencerId.get(account.client_influencer_id)
            : null;
        const exploreAccount = result?.platforms[account.platform].find(
            (candidate) => candidate.creatorbase_client_influencer_account_id === account.client_influencer_account_id
        );

        return exploreAccount ?? null;
    };
};

/**
 * The platform users behind the accounts on `platform`, once each, which is what their images are
 * looked up by. Only an account Creator Explore lists has one.
 */
export const getAccountUserIds = (
    accounts: CreatorAccountRef[],
    exploreResults: influencer.CreatorSearchResult[],
    platform: creatorbase.CreatorBulkUploadItemPlatform
): number[] => {
    const findExploreAccount = getExploreAccountFinder(exploreResults);
    const ids = accounts
        .filter((account) => account.platform === platform)
        .map((account) => findExploreAccount(account)?.creatorbase_user_id)
        .filter(isNumber);

    return [...new Set(ids)];
};

/** The image an account is shown with, the cached copy when there is one. */
export const getImageUrl = (image: { avatar_thumb: CachedImage }): string =>
    image.avatar_thumb.cached_url ?? image.avatar_thumb.original_url;

/** Pairs each account with itself among the Creator Explore results, and with its image. */
export const withExploreAccountDetails = <T extends CreatorAccountRef>(
    accounts: T[],
    exploreResults: influencer.CreatorSearchResult[],
    imageUrlsByUserId: ImageUrlsByUserId
): (T & ExploreAccountDetails)[] => {
    const findExploreAccount = getExploreAccountFinder(exploreResults);

    return accounts.map((account) => {
        const exploreAccount = findExploreAccount(account);
        const imageUrl = exploreAccount
            ? imageUrlsByUserId[account.platform].get(exploreAccount.creatorbase_user_id) ?? null
            : null;

        return { ...account, exploreAccount, imageUrl };
    });
};
